<template>
  <div
    :class="`booking-search-controls booking-search-controls${
      full_width ? '--full-width' : '--inline'
    }`"
  >
    <b-row>
      <!-- Occupancy Selector -->
      <b-col
        class="booking-search-controls__occupancy"
        cols="12"
        :lg="full_width ? 2 : 8"
      >
        <traveler-occupancy-input
          :travelers="travelers"
          :max_adults="max_adults"
          :max_children="max_children"
          :occupancy_alignment="full_width ? 'end' : 'auto'"
          @update:travelers="on_travelers_update"
          @update:adults="on_adults_update"
          @update:children="on_children_update"
        />
      </b-col>

      <!-- Search Button -->
      <b-col
        v-b-popover.hover.bottom="search_block_message"
        class="booking-search-controls__search"
        cols="12"
        :lg="full_width ? 1 : 4"
      >
        <vb-button
          :vb_options="{
            text: loading ? 'Attendi...' : 'Cerca',
            disabled: loading || !can_search_now || disabled,
            variant: 'info',
            block: true,
          }"
          @click="handle_search_click"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex';
  import { get_config } from '@/utilities/';
  import { is_origin_destination_valid } from '@liveflight/utilities/origin-destination';
  import TravelerOccupancyInput from './traveler-occupancy-input.vue';

  export default {
    name: 'booking-search-controls',
    components: {
      'traveler-occupancy-input': TravelerOccupancyInput,
    },
    props: {
      // External disable override (optional)
      disabled: {
        type: Boolean,
        default: false,
      },
      // Full width layout mode
      full_width: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      ...mapState('liveflight', ['adults', 'children', 'travelers']),
      ...mapGetters('liveflight', [
        'loading',
        'origin_destinations',
        'return_flight',
      ]),
      // Configuration limits
      max_adults() {
        return get_config('guiSettings.searchMaxAdults', 6);
      },
      max_children() {
        return get_config('guiSettings.searchMaxChildren', 4);
      },
      // Validity result per leg — single source for the button state and the hint
      origin_destinations_validity() {
        return this.origin_destinations.map(is_origin_destination_valid);
      },
      // Validate if search is possible
      can_search_now() {
        return (
          !this.loading &&
          this.origin_destinations_validity.every((result) => result.valid)
        );
      },
      // First blocking reason, surfaced on the disabled search button
      search_block_message() {
        if (this.loading) {
          return '';
        }
        const first_invalid = this.origin_destinations_validity.find(
          (result) => !result.valid
        );
        return first_invalid ? first_invalid.message : '';
      },
    },
    methods: {
      on_travelers_update(value) {
        this.$store.commit('liveflight/travelers', value);
        this.$emit('occupancy-change', {
          travelers: value,
          adults: this.adults,
          children: this.children,
        });
      },
      on_adults_update(value) {
        this.$store.commit('liveflight/adults', value);
        this.$emit('occupancy-change', {
          travelers: this.travelers,
          adults: value,
          children: this.children,
        });
      },
      on_children_update(value) {
        this.$store.commit('liveflight/children', value);
        this.$emit('occupancy-change', {
          travelers: this.travelers,
          adults: this.adults,
          children: value,
        });
      },
      handle_search_click() {
        if (this.can_search_now && !this.disabled) {
          this.$emit('search');
          this.track_form({
            event: 'booking-engine:search:submit',
            data: { type: 'info' },
          });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .booking-search-controls {
    $this: &;

    &__occupancy {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      margin-top: $grid-gutter-width / 10;
      margin-bottom: $grid-gutter-width / 2;

      @include media-breakpoint-up(lg) {
        margin-top: 0;
        margin-bottom: 0;
        justify-content: flex-end;
        padding-left: 0;
      }
    }

    &--inline {
      @include media-breakpoint-up(lg) {
        & > * {
          padding-left: 0 !important;
        }
      }
    }

    &--full-width {
      width: 100%;

      #{$this}__occupancy {
        @include media-breakpoint-up(lg) {
          padding-left: 15px;
        }
      }

      @include media-breakpoint-up(lg) {
        display: flex;
        justify-content: space-between;

        & > *:not(:nth-child(1)) {
          padding-left: 0 !important;
        }
      }

      & .row {
        @include media-breakpoint-up(lg) {
          width: 100%;
          justify-content: space-between;
        }
      }
    }

    &__search {
      @include media-breakpoint-up(lg) {
        padding-left: 0;
      }

      #{$this}--full-width & {
        @include media-breakpoint-up(lg) {
          padding-right: 0;
          margin-right: -$grid-gutter-width / 2;
        }
      }

      ::v-deep .vb-btn {
        width: 100%;
      }
    }
  }
</style>
